import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { formatDateTimeForDisplay } from "../../../utils/dateUtils";
import { useTranslation } from "react-i18next";
import { formatPriceWithDollar } from "../../../utils/currency";
import ReceiptDrawer from "../components/ReceiptDrawer";

/** Adjusted column: Adj () only when API description is exactly "Misc Fee Dec" (case-insensitive). */
const isMiscFeeDecDescription = (desc) =>
  String(desc ?? "").trim().toLowerCase() === "misc fee dec";

const TransactionsTab = ({ data, accountData, activeColor = "secondary", openDrawer, canViewTransactions = true }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      setIsDarkMode((prev) => (prev !== newTheme ? newTheme : prev));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  const txExpandedStyles = useMemo(
    () => ({
      panel: {
        borderTop: isDarkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #eee",
        backgroundColor: isDarkMode ? "#27293d" : "#fafafa",
      },
      table: {
        fontSize: "0.75rem",
        color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : undefined,
      },
      headerMuted: {
        fontWeight: "600",
        color: isDarkMode ? "rgba(255, 255, 255, 0.65)" : undefined,
      },
      collected: {
        fontWeight: "bold",
        color: isDarkMode ? "#6eb5ff" : "#007bff",
      },
      collectedCell: isDarkMode ? "#6eb5ff" : "#007bff",
      mutedNote: {
        fontSize: "0.75rem",
        alignSelf: "center",
        color: isDarkMode ? "rgba(255, 255, 255, 0.55)" : undefined,
      },
      effectDate: isDarkMode ? "rgba(255, 255, 255, 0.65)" : "#666",
    }),
    [isDarkMode]
  );

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (!canViewTransactions) {
      setRows([]);
      return;
    }
    if (!data) return;
    const normalized = data.map((transaction, idx) => ({
      id: transaction?.id || transaction?.documentId || idx + 1,
      date: transaction?.date || "-",
      effect_date:
        transaction?.effect_date ||
        (transaction?.date && transaction.date !== "-" ? transaction.date : null) ||
        "-",
      amount: Number(transaction?.amount) || 0,
      description: transaction?.description || "-",
      collected: transaction?.collected !== undefined ? transaction?.collected : (transaction?.amount || 0),
      posted_by: transaction?.created_user?.user_detail?.full_name || "-",
      beginningBalance: transaction?.beg_balance || 0,
      endingBalance: transaction?.end_balance || 0,
      taxAmount: transaction?.tax_amount || 0,
      sales_tax_beg_bal: transaction?.sales_tax_beg_bal || 0,
      sales_tax_end_bal: transaction?.sales_tax_end_bal || 0,
      lateFeesAmount: transaction?.late_fees_amount || 0,
      late_fees_amount: transaction?.late_fees_amount || 0,
      misc_fee_collected: transaction?.misc_fee_collected || 0,
      paymentForm: transaction?.payment_form || "-",
      refNumber: transaction?.ref_number || transaction?.refNumber || "-",
      receipt_id: transaction?.receipt_id || "",
      createdUser: transaction?.created_user || null,
      actionType: transaction?.action_type || "-",
      deliveryMethod: transaction?.delivery_method || "-",
      note: transaction?.note || "-",
      payment_type: transaction?.payment_type || "",
      cpi_amount: transaction?.cpi_amount || 0,
      cpi_beg_bal: transaction?.cpi_beg_bal || 0,
      cpi_end_bal: transaction?.cpi_end_bal || 0,
      // def_down specific fields
      def_down_beg_bal: transaction?.def_down_beg_bal ?? 0,
      def_down_end_bal: transaction?.def_down_end_bal ?? 0,
      def_down_collected: transaction?.def_down_collected ?? 0,
      // prin specific fields
      beg_balance: transaction?.beg_balance ?? 0,
      end_balance: transaction?.end_balance ?? 0,
      adjusted: transaction?.adjusted || 0,
      // principal line breakdown (new API); omit on old transactions → all undefined/null
      principal_beg_balance: transaction?.principal_beg_balance,
      principal_collected: transaction?.principal_collected,
      principal_adjusted: transaction?.principal_adjusted,
      principal_end_balance: transaction?.principal_end_balance,
      // sales tax fields
      sales_tax_beg_bal: transaction?.sales_tax_beg_bal || 0,
      sales_tax_end_bal: transaction?.sales_tax_end_bal || 0,
      // non earn prin fields
      non_earn_prin_beg_bal: transaction?.non_earn_prin_beg_bal || 0,
      non_earn_prin_end_bal: transaction?.non_earn_prin_end_bal || 0,
      non_earn_prin_collected: transaction?.non_earn_prin_collected ?? 0,
      non_earn_prin_adjusted: transaction?.non_earn_prin_adjusted ?? 0,
      // cpi fields
      cpi_beg_bal: transaction?.cpi_beg_bal || 0,
      cpi_end_bal: transaction?.cpi_end_bal || 0,
      // def down fields
      def_down_beg_bal: transaction?.def_down_beg_bal || 0,
      def_down_end_bal: transaction?.def_down_end_bal || 0,
      def_down_collected: transaction?.def_down_collected || 0,
      def_down_adjusted: transaction?.def_down_adjusted ?? 0,
      // misc fees fields
      misc_fees_beg_bal: transaction?.misc_fee_beg_bal || 0,
      misc_fees_end_bal: transaction?.misc_fee_end_bal || 0,
      misc_fees_collected: transaction?.misc_fee_collected || 0,
      misc_fees_adjusted: transaction?.misc_fee_adjusted || 0,
      late_fee_beg_bal: transaction?.late_fee_beg_bal || 0,
      late_fee_end_bal: transaction?.late_fee_end_bal || 0,
      late_fee_collected: transaction?.late_fee_collected || 0,
      late_fee_adjusted: transaction?.late_fee_adjusted || 0,
      interest_beg_bal: transaction?.interest_beg_bal ?? 0,
      interest_collected: transaction?.interest_collected ?? 0,
      interest_adjusted: transaction?.interest_adjusted ?? 0,
      interest_rebated: transaction?.interest_rebated ?? 0,
      interest_end_bal: transaction?.interest_end_bal ?? 0,
      // detail array for receipt breakdown
      detail: Array.isArray(transaction?.detail) ? transaction.detail : null,
      migration_receipt_allocation:
        transaction?.external_payment?.migration_receipt_allocation ||
        transaction?.migration_receipt_allocation ||
        null,
      external_payment: transaction?.external_payment || null,
      originalData: transaction,
    }));
    setRows(normalized);

  }, [data, canViewTransactions]);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const columns = useMemo(() => [
    {
      Header: t('transactions.postedBy'),
      accessor: "posted_by",
      Cell: ({ value }) => value || "-",
      width: 150,
      minWidth: 150,
      maxWidth: 150,
    },
    {
      Header: t('transactions.postDateEffectDate'),
      accessor: "date",
      Cell: ({ row }) => {
        const postDate = formatDateTimeForDisplay(row.original.date);
        const effectDate = formatDateTimeForDisplay(row.original.effect_date);
        return (
          <div>
            <div>{postDate}</div>
            <div style={{ fontSize: "0.85em", color: txExpandedStyles.effectDate }}>{effectDate}</div>
          </div>
        );
      },
      width: 150,
    },
    {
      Header: t('transactions.begAcctBalance'),
      accessor: "beginningBalance",
      Cell: ({ value }) => formatPriceWithDollar(value),
      width: 140,
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: t('transactions.collected'),
      accessor: "amount",
      Cell: ({ row, value }) => {
        const isReversal = row.original.payment_type === "reversal" || row.original.payment_type === "def_down_reversal";
        const formatted = formatPriceWithDollar(value);
        if (isReversal) {
          return <span style={{ color: txExpandedStyles.collectedCell, fontWeight: "bold", fontSize: "13px" }}>{formatted}</span>;
        }
        const displayValue = value < 0 ? formatted.replace('$', '').replace(',', '') : formatted;
        // Late fee / misc fee ledger rows are adjustments — Collected stays $0.00
        if (
          row.original.payment_type === "late_fee_assessed" ||
          row.original.payment_type === "misc_fee"
        ) {
          return <span style={{ color: txExpandedStyles.collectedCell, fontWeight: "bold", fontSize: "13px" }}>$0.00</span>;
        }
        return (
          <span style={{ color: txExpandedStyles.collectedCell, fontWeight: "bold", fontSize: "13px" }}>
            {value === 0 ? "$0.00" : "(" + displayValue.replace(/[()]/g, '') + ")"}
          </span>
        );
      },
      width: 95,
      minWidth: 95,
      maxWidth: 95,
    },
    {
      Header: t('transactions.adjusted'),
      accessor: "adjusted",
      Cell: ({ row, value }) => {
        const rawDesc = String(
          row.original?.originalData?.description ??
            row.original?.description ??
            ""
        ).trim();
        const n = Number(value) || 0;
        const formatted = formatPriceWithDollar(Math.abs(n));
        if (isMiscFeeDecDescription(rawDesc)) {
          return `(${formatted})`;
        }
        // Payoff write-off shortfall is stored positive on payment.adjusted
        if (row.original?.payment_type === "payoff" && n > 0.009) {
          return `(${formatted})`;
        }
        if (n < -0.009) {
          return `(${formatted})`;
        }
        return formatPriceWithDollar(n);
      },
      width: 95,
      minWidth: 95,
      maxWidth: 95,
    },
    {
      Header: t('transactions.endAcctBalance'),
      accessor: "endingBalance",
      Cell: ({ value }) => formatPriceWithDollar(value),
      width: 140,
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: (
        <div style={{ lineHeight: 1.15 }}>
          <div>{t("transactions.pmtMethod")}</div>
          <div>{t("transactions.transDesc")}</div>
        </div>
      ),
      accessor: "paymentForm",
      Cell: ({ row }) => {
        const { original } = row;
        return (
          <div className="text-center">
            <div>{original.paymentForm || "-"}</div>
            <div>{original?.description}</div>
          </div>
        );
      },
      width: 140,
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: (
        <div className="text-left" style={{ lineHeight: 1.15 }}>
          <div>{t("transactions.comments")}</div>
          <div>{t("transactions.ref")}</div>
        </div>
      ),
      accessor: "refNumber",
      Cell: ({ row }) => {
        const { original } = row;
        const note = original.note && original.note !== "-" ? original.note : "";
        const ref = original.refNumber && original.refNumber !== "-" ? original.refNumber : "";
        return (
          <div className="text-left">
            <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
              {note}
            </div>
            <div style={{ color: txExpandedStyles.effectDate }}>{ref}</div>
          </div>
        );
      },
      width: 210,
      minWidth: 180,
      maxWidth: 240,
    },
  ], [t, txExpandedStyles]);

  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const handleOpenReceipt = (original) => {
    setSelectedTransaction(original);
    setIsReceiptOpen(true);
  };

  const handleOpenEmail = (original) => {
    openDrawer("send", {
      transaction_id: original.id,
      transaction: original,
      accountData: accountData,
    });
  };

  const renderExpandedContent = (original) => {
    const isDefDown = original.payment_type === "def_down" || original.payment_type === "def_down_reversal" || original.payment_type === "reversal";
    const isReversal = original.payment_type === "reversal" || original.payment_type === "def_down_reversal";

    const usePrincipalLineBreakdown = [
      original.principal_beg_balance,
      original.principal_collected,
      original.principal_adjusted,
      original.principal_end_balance,
    ].some((v) => v != null);

    const principalBegBal = usePrincipalLineBreakdown
      ? (original.principal_beg_balance != null ? original.principal_beg_balance : original.beg_balance)
      : original.beg_balance;
    const principalCollectedAmt = usePrincipalLineBreakdown
      ? (original.principal_collected != null ? original.principal_collected : original.collected)
      : original.collected;
    const principalEndBal = usePrincipalLineBreakdown
      ? (original.principal_end_balance != null ? original.principal_end_balance : original.end_balance)
      : original.end_balance;
    const formatAdjustedCell = (value) => {
      const n = Number(value) || 0;
      if (Math.abs(n) < 0.009) return "$0.00";
      // Write-off reductions are stored negative (end = beg - collected + adjusted)
      if (n < 0) return `(${formatPriceWithDollar(Math.abs(n))})`;
      return formatPriceWithDollar(n);
    };

    const principalAdjustedCell =
      usePrincipalLineBreakdown && original.principal_adjusted != null
        ? formatAdjustedCell(original.principal_adjusted)
        : "$0.00";

    return (
      <div className="p-3" style={txExpandedStyles.panel}>
        <Row>
          <Col md="12">
            <table className="table table-sm table-borderless mb-0" style={txExpandedStyles.table}>
              <thead>
                <tr>
                  <th className="py-0 border-0"></th>
                  <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>Beg Bal</th>
                  <th className="py-0 border-0" style={txExpandedStyles.collected}>Collected</th>
                  <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>Adjusted</th>
                  <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>Rebated</th>
                  <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>End Bal</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Down Payment:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.def_down_beg_bal)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>
                    {original.def_down_collected !== 0 ? (isReversal ? formatPriceWithDollar(original.def_down_collected) : `(${formatPriceWithDollar(original.def_down_collected)})`) : "$0.00"}
                  </td>
                  <td className="py-0 border-0">{original.def_down_adjusted !== 0 ? formatAdjustedCell(original.def_down_adjusted) : "$0.00"}</td>
                  <td className="py-0 border-0"></td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.def_down_end_bal)}</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Prin:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(principalBegBal)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>
                    {principalCollectedAmt !== 0
                      ? (isReversal ? formatPriceWithDollar(principalCollectedAmt) : `(${formatPriceWithDollar(principalCollectedAmt)})`)
                      : "$0.00"}
                  </td>
                  <td className="py-0 border-0">{principalAdjustedCell}</td>
                  <td className="py-0 border-0"></td>
                  <td className="py-0 border-0">{formatPriceWithDollar(principalEndBal)}</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Non Earn Prin:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.non_earn_prin_beg_bal || 0)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>
                    {original.non_earn_prin_collected !== 0
                      ? (isReversal ? formatPriceWithDollar(original.non_earn_prin_collected) : `(${formatPriceWithDollar(original.non_earn_prin_collected)})`)
                      : "$0.00"}
                  </td>
                  <td className="py-0 border-0">{formatAdjustedCell(original.non_earn_prin_adjusted)}</td>
                  <td className="py-0 border-0"></td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.non_earn_prin_end_bal || 0)}</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Sales Tax:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.sales_tax_beg_bal)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>
                    {original.taxAmount !== 0 ? (isReversal ? formatPriceWithDollar(original.taxAmount) : `(${formatPriceWithDollar(original.taxAmount)})`) : "$0.00"}
                  </td>
                  <td className="py-0 border-0">
                    {(() => {
                      // Positive remaining cleared without cash (= write-off magnitude).
                      // Store display via negative adj so formatAdjustedCell shows ($x).
                      const taxWriteOff =
                        (Number(original.sales_tax_beg_bal) || 0) -
                        (Number(original.taxAmount) || 0) -
                        (Number(original.sales_tax_end_bal) || 0);
                      return formatAdjustedCell(-taxWriteOff);
                    })()}
                  </td>
                  <td className="py-0 border-0"></td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.sales_tax_end_bal)}</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">UnEarned Int:</td>
                  <td className="py-0 border-0">$0.00</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>$0.00</td>
                  <td className="py-0 border-0">$0.00</td>
                  <td className="py-0 border-0">
                    {(original.interest_rebated ?? 0) !== 0
                      ? `(${formatPriceWithDollar(Math.abs(Number(original.interest_rebated) || 0))})`
                      : "$0.00"}
                  </td>
                  <td className="py-0 border-0">$0.00</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Interest:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.interest_beg_bal ?? 0)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>
                    {(original.interest_collected ?? 0) !== 0
                      ? (isReversal
                        ? formatPriceWithDollar(original.interest_collected)
                        : `(${formatPriceWithDollar(original.interest_collected)})`)
                      : "$0.00"}
                  </td>
                  <td className="py-0 border-0">{formatAdjustedCell(original.interest_adjusted ?? 0)}</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.interest_rebated ?? 0)}</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.interest_end_bal ?? 0)}</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Late Fees:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.late_fee_beg_bal)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>
                    {original.late_fee_collected !== 0 ? (isReversal ? formatPriceWithDollar(original.late_fee_collected) : `(${formatPriceWithDollar(original.late_fee_collected)})`) : "$0.00"}
                  </td>
                  <td className="py-0 border-0">{formatAdjustedCell(original.late_fee_adjusted)}</td>
                  <td className="py-0 border-0"></td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.late_fee_end_bal)}</td>
                </tr>
                <tr>
                  <td className="text-right py-0 border-0 font-weight-bold">Misc Fees:</td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.misc_fees_beg_bal)}</td>
                  <td className="py-0 border-0" style={txExpandedStyles.collected}>{formatPriceWithDollar(original.misc_fees_collected)}</td>
                  <td className="py-0 border-0">{formatAdjustedCell(original.misc_fees_adjusted)}</td>
                  <td className="py-0 border-0"></td>
                  <td className="py-0 border-0">{formatPriceWithDollar(original.misc_fees_end_bal)}</td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md="12" className="d-flex align-items-center justify-content-start">
            <div className="d-flex" style={{ gap: '10px' }}>
              <Button outline color="info" size="sm" style={{ fontWeight: 'bold', borderWidth: '1px' }} onClick={() => handleOpenReceipt(original)}>
                {t('transactions.receipt')}
              </Button>
              {accountData?.customer?.email ? (
                <Button outline color="info" size="sm" style={{ fontWeight: 'bold', borderWidth: '1px' }} onClick={() => handleOpenEmail(original)}>
                  {t('transactions.emailReceipt')}
                </Button>
              ) : (
                <span className="text-muted" style={txExpandedStyles.mutedNote}>
                  {t('transactions.missingEmail')}
                </span>
              )}
            </div>
          </Col>
        </Row>
      </div>
    );
  };



  return (
    <div className="content account-transactions-tab">
      {isDarkMode && (
        <style>
          {`
            body:not(.white-content) .account-transactions-tab .ReactTable .rt-tbody .rt-td.p-0 {
              background-color: #27293d !important;
            }
            body:not(.white-content) .account-transactions-tab .ReactTable.-striped .rt-tbody .rt-tr .rt-td {
              color: rgba(255, 255, 255, 0.9);
            }
          `}
        </style>
      )}
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <h2 className="mb-0">
                {t('common.recordsInTotal', 'Total Records')}: {rows.length}
              </h2>
            </CardHeader>
            <CardBody className="pt-0">
              {!canViewTransactions ? (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td className="text-center text-muted py-5" style={{ fontWeight: 600 }}>
                          {t('transactions.securityRequiredToView')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : !rows || rows.length === 0 ? (
                <div className="text-muted">{t('transactions.noTransactionsFound')}</div>
              ) : (
                <>
                <div style={{fontSize:13}}>

                  <ReactTable
                    activeColor={activeColor}
                    data={paginatedData}
                    columns={columns}
                    className="-striped -highlight"
                    renderExpandedRow={(original) => renderExpandedContent(original)}
                    isRowExpanded={(original) => !!expandedRows[original.id]}
                    onRowClick={(original) => toggleExpand(original.id)}
                    manualPagination={true}
                    pageCount={Math.ceil(rows.length / pageSize)}
                    pageIndex={currentPage}
                    pageSize={pageSize}
                    onPageChange={(index) => setCurrentPage(index)}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setCurrentPage(0);
                    }}
                  />
                  <div className="mt-2 text-right">
                    <small>{rows.length} {t('transactions.recordsInTotal')}</small>
                  </div>
                </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ReceiptDrawer
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        transaction={selectedTransaction}
        accountData={accountData}
      />
    </div>
  );
};

export default TransactionsTab;


